import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
  type CollectionReference,
  type DocumentData,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import type { EndpointDef, PermissionGroupDef } from "@/features/users/data/endpoints";

const API_PERMISSIONS_COLLECTION = "api_permissions";
const API_PERMISSION_GROUPS_COLLECTION = "api_permission_groups";

export interface CreatePermissionGroupInput {
  id: string;
  label: string;
  description?: string;
  permissionIds: string[];
  order?: number;
}

export type UpdatePermissionGroupInput = Partial<
  Pick<CreatePermissionGroupInput, "id" | "label" | "description" | "permissionIds" | "order">
>;

export interface CreateApiPermissionInput {
  id: string;
  method: string;
  label: string;
  category: string;
  auth: "apiKey" | "none";
  description?: string;
}

export type UpdateApiPermissionInput = Partial<CreateApiPermissionInput>;

function getPermissionsCollection(): CollectionReference<DocumentData> {
  return collection(db, API_PERMISSIONS_COLLECTION);
}

function getGroupsCollection(): CollectionReference<DocumentData> {
  return collection(db, API_PERMISSION_GROUPS_COLLECTION);
}

function mapAuth(value: unknown): "apiKey" | "none" {
  return value === "apiKey" ? "apiKey" : "none";
}

/** Normaliza el path HTTP: trim + garantiza `/` inicial. */
function normalizePermissionPath(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function assertPermissionFields(input: {
  id: string;
  label: string;
  method: string;
  category: string;
}): { id: string; label: string; method: string; category: string } {
  const id = normalizePermissionPath(input.id);
  const label = input.label.trim();
  const method = input.method.trim().toUpperCase();
  const category = input.category.trim() || "Sin categoría";

  if (!id) {
    throw new Error("El path (id) del endpoint es obligatorio");
  }
  if (!label) {
    throw new Error("El label del endpoint es obligatorio");
  }
  if (!method) {
    throw new Error("El method del endpoint es obligatorio");
  }

  return { id, label, method, category };
}

async function assertUniquePermissionPath(
  path: string,
  excludeDocId?: string
): Promise<void> {
  const existing = await listApiPermissions();
  const clash = existing.find(
    (ep) => ep.id === path && ep.docId !== excludeDocId
  );
  if (clash) {
    throw new Error(`Ya existe un endpoint con path "${path}"`);
  }
}

/**
 * Lista el catálogo de permisos individuales desde Firestore.
 */
export async function listApiPermissions(): Promise<EndpointDef[]> {
  const snapshot = await getDocs(getPermissionsCollection());

  const items: EndpointDef[] = snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      docId: docSnap.id,
      id: typeof data.id === "string" ? data.id : "",
      method: typeof data.method === "string" ? data.method : "",
      label: typeof data.label === "string" ? data.label : data.id ?? docSnap.id,
      category: typeof data.category === "string" ? data.category : "Sin categoría",
      auth: mapAuth(data.auth),
      description: typeof data.description === "string" ? data.description : undefined,
    };
  });

  return items
    .filter((ep) => ep.id.length > 0)
    .sort((a, b) => {
      const byCategory = a.category.localeCompare(b.category, "es");
      if (byCategory !== 0) return byCategory;
      return a.id.localeCompare(b.id, "es");
    });
}

/**
 * Crea un permiso individual en `api_permissions` (doc id auto).
 */
export async function createApiPermission(
  input: CreateApiPermissionInput
): Promise<EndpointDef> {
  const fields = assertPermissionFields(input);
  await assertUniquePermissionPath(fields.id);

  const payload = {
    id: fields.id,
    method: fields.method,
    label: fields.label,
    category: fields.category,
    auth: mapAuth(input.auth),
    description: input.description?.trim() || null,
  };

  const ref = await addDoc(getPermissionsCollection(), payload);

  return {
    docId: ref.id,
    id: payload.id,
    method: payload.method,
    label: payload.label,
    category: payload.category,
    auth: payload.auth,
    description: payload.description ?? undefined,
  };
}

/**
 * Actualiza un permiso individual por document id.
 */
export async function updateApiPermission(
  docId: string,
  input: UpdateApiPermissionInput
): Promise<void> {
  const ref = doc(db, API_PERMISSIONS_COLLECTION, docId);
  const payload: Record<string, unknown> = {};

  if (input.id !== undefined) {
    const path = normalizePermissionPath(input.id);
    if (!path) {
      throw new Error("El path (id) del endpoint es obligatorio");
    }
    await assertUniquePermissionPath(path, docId);
    payload.id = path;
  }
  if (input.method !== undefined) {
    const method = input.method.trim().toUpperCase();
    if (!method) {
      throw new Error("El method del endpoint es obligatorio");
    }
    payload.method = method;
  }
  if (input.label !== undefined) {
    const label = input.label.trim();
    if (!label) {
      throw new Error("El label del endpoint es obligatorio");
    }
    payload.label = label;
  }
  if (input.category !== undefined) {
    payload.category = input.category.trim() || "Sin categoría";
  }
  if (input.auth !== undefined) {
    payload.auth = mapAuth(input.auth);
  }
  if (input.description !== undefined) {
    payload.description = input.description.trim() || null;
  }

  if (Object.keys(payload).length === 0) return;
  await updateDoc(ref, payload);
}

/**
 * Elimina un permiso y lo saca de `permissionIds` en grupos que lo referencien.
 */
export async function deleteApiPermission(docId: string): Promise<void> {
  const groups = await listPermissionGroups();
  const affected = groups.filter((g) => g.permissionIds.includes(docId));

  await Promise.all(
    affected.map((group) =>
      updatePermissionGroup(group.docId, {
        permissionIds: group.permissionIds.filter((id) => id !== docId),
      })
    )
  );

  await deleteDoc(doc(db, API_PERMISSIONS_COLLECTION, docId));
}

/**
 * Lista los grupos de permisos desde Firestore.
 */
export async function listPermissionGroups(): Promise<PermissionGroupDef[]> {
  const snapshot = await getDocs(getGroupsCollection());

  const items: PermissionGroupDef[] = snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    const permissionIds = Array.isArray(data.permissionIds)
      ? data.permissionIds.filter((id): id is string => typeof id === "string")
      : [];

    return {
      docId: docSnap.id,
      id: typeof data.id === "string" ? data.id : docSnap.id,
      label: typeof data.label === "string" ? data.label : data.id ?? docSnap.id,
      description: typeof data.description === "string" ? data.description : undefined,
      permissionIds,
      order: typeof data.order === "number" ? data.order : undefined,
    };
  });

  return items.sort((a, b) => {
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return a.label.localeCompare(b.label, "es");
  });
}

/**
 * Crea un grupo en `api_permission_groups` (doc id auto).
 */
export async function createPermissionGroup(
  input: CreatePermissionGroupInput
): Promise<PermissionGroupDef> {
  const payload = {
    id: input.id.trim(),
    label: input.label.trim(),
    description: input.description?.trim() || null,
    permissionIds: input.permissionIds,
    ...(typeof input.order === "number" ? { order: input.order } : {}),
  };

  const ref = await addDoc(getGroupsCollection(), payload);

  return {
    docId: ref.id,
    id: payload.id,
    label: payload.label,
    description: payload.description ?? undefined,
    permissionIds: payload.permissionIds,
    order: input.order,
  };
}

/**
 * Actualiza un grupo existente por document id.
 */
export async function updatePermissionGroup(
  docId: string,
  input: UpdatePermissionGroupInput
): Promise<void> {
  const ref = doc(db, API_PERMISSION_GROUPS_COLLECTION, docId);
  const payload: Record<string, unknown> = {};

  if (input.id !== undefined) payload.id = input.id.trim();
  if (input.label !== undefined) payload.label = input.label.trim();
  if (input.description !== undefined) {
    payload.description = input.description.trim() || null;
  }
  if (input.permissionIds !== undefined) payload.permissionIds = input.permissionIds;
  if (input.order !== undefined) payload.order = input.order;

  await updateDoc(ref, payload);
}

/**
 * Elimina un grupo por document id.
 */
export async function deletePermissionGroup(docId: string): Promise<void> {
  await deleteDoc(doc(db, API_PERMISSION_GROUPS_COLLECTION, docId));
}
